/* -----------------Globals--------------- */
import { useNavigation, usePathname, useRouter } from "expo-router";

/* -----------------UI--------------- */
import { Appbar, type AppbarHeaderProps, Text } from "react-native-paper";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { FeedbackDialog } from "@features/feedback/components/dialogs/FeedbackDialog";
import { defaultFeedbackSubtitle } from "@features/feedback/constants";
import { isWeb } from "@helpers/platform";
/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import AppbarAction from "@memorang/ui/src/components/AppbarAction";
import AvatarGenerator from "@memorang/ui/src/components/AvatarGenerator/AvatarGenerator";
import Box from "@memorang/ui/src/components/Box";
import { CustomBreadcrumb } from "@memorang/ui/src/components/CustomBreadcrumb";
import type { BreadcrumbType } from "@memorang/ui/src/components/CustomBreadcrumbWeb";
import TooltipWrapper from "@memorang/ui/src/components/TooltipWrapper";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RowStats, { type Stat } from "../RowStats";
import { CommandBar } from "./CommandBar";
const TitleContent = ({
	headerTitle,
	subtitle,
	stats,
	breadcrumbs,
	renderSearchbar,
}: {
	align?: "center" | "flex-start";
	headerTitle?: string;
	subtitle?: string | React.ReactNode;
	stats?: Stat[];
	breadcrumbs?: BreadcrumbType[];
	renderSearchbar?: () => React.ReactNode;
}) => {
	const { isMobile, isMediumWindowSize } = useDevice();
	return (
		<Box gap={isMobile || isMediumWindowSize ? 8 : 16} width={"100%"}>
			{renderSearchbar?.()}
			<Box gap={4} paddingTop={isMobile ? 0 : 16}>
				{breadcrumbs?.length && !isMobile && isWeb ? (
					<CustomBreadcrumb breadcrumbs={breadcrumbs} size="small" />
				) : null}
				<Box>
					{headerTitle && (
						<Text
							numberOfLines={1}
							style={{ maxWidth: isMobile ? 400 : "100%" }}
							variant={isMobile ? "titleLarge" : "headlineMedium"}
						>
							{headerTitle as string}
						</Text>
					)}
					{subtitle && <Text variant="bodyMedium">{subtitle}</Text>}
				</Box>
			</Box>
			{stats?.length && !isMobile ? (
				<RowStats stacked={false} stats={stats} maxWidth={150} />
			) : null}
		</Box>
	);
};

const CustomNavigationBar = (
	props: {
		back?: boolean;
		stats?: Stat[];
		breadcrumbs?: BreadcrumbType[];
		showHambugerMenu?: boolean;
		showCommandBar?: boolean;
		showSettings?: boolean;
		showUserMenu?: boolean;
		renderSearchbar?: () => React.ReactNode;
		align?: "center" | "flex-start";
		options: {
			headerTitle?: string;
			subtitle?: string | React.ReactNode;
			headerRight?: () => React.ReactNode;
			headerLeft?: () => React.ReactNode;
		};
	} & Omit<AppbarHeaderProps, "children">,
) => {
	const {
		back,
		stats,
		options: { headerTitle, subtitle, headerRight, headerLeft },
		breadcrumbs,
		renderSearchbar,
		showHambugerMenu,
		align,
		showCommandBar,
		showSettings = true,
		showUserMenu = true,
		...rest
	} = props;
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const pathname = usePathname();

	const { handleFeedbackNavigation } = useCustomNavigation();
	const { newUserInfo } = useAuthContext();

	const toggleShowFeedbackDialog = useCallback(() => {
		setShowFeedbackDialog((prev) => !prev);
	}, []);

	const { isMobile, isMediumWindowSize } = useDevice();

	// FOR SOME REASON, the medium prop medium does not work well on tablet
	const mode = isMobile ? "small" : isMediumWindowSize ? "large" : "medium";
	const finalAlign =
		align || (isMobile ? ("center" as const) : ("flex-start" as const));

	const renderHeaderRight = useMemo(() => {
		if (headerRight) {
			return headerRight();
		}

		return (
			<Box
				flexDirection="row"
				paddingRight={12}
				alignItems="center"
				accessibilityRole="header"
			>
				{isWeb ? (
					<TooltipWrapper title="We value your feedback. Click here to share your thoughts.">
						<AppbarAction
							accessibilityRole="button"
							icon="comment-quote-outline"
							onPress={() => handleFeedbackNavigation(toggleShowFeedbackDialog)}
						/>
					</TooltipWrapper>
				) : (
					<AppbarAction
						accessibilityRole="button"
						icon="comment-quote-outline"
						onPress={() => handleFeedbackNavigation(toggleShowFeedbackDialog)}
					/>
				)}
				{/* {showSettings && (
					<Appbar.Action
						icon="cog-outline"
						style={{ marginRight: 16 }}
						onPress={() => router.push("/(protected)/account/settings")}
					/>
				)} */}
				{showUserMenu && (
					<Appbar.Action
						icon={() => (
							<AvatarGenerator
								avatarType="initials"
								size={24}
								firstName={newUserInfo?.firstName || newUserInfo?.name}
								lastName={newUserInfo?.lastName}
								customStyle={{
									position: "relative",
									top: undefined,
								}}
							/>
						)}
						onPress={() => router.push("/(protected)/account/settings")}
					/>
				)}
			</Box>
		);
	}, [
		headerRight,
		newUserInfo,
		showUserMenu,
		handleFeedbackNavigation,
		toggleShowFeedbackDialog,
	]);

	const router = useRouter();
	const navigation = useNavigation<{
		toggleDrawer: () => void;
	}>();
	return (
		//@ts-ignore
		<Appbar.Header
			theme={theme}
			mode={mode}
			elevated
			accessibilityRole="header"
			{...rest}
			{...(isWeb
				? {
						style: {
							height: "fit-content",
						},
					}
				: {})}
		>
			{isWeb && (
				<Box
					position="absolute"
					alignItems="center"
					left={0}
					flexDirection="row"
					accessibilityRole="header"
				>
					{isMobile && showHambugerMenu && (
						<AppbarAction
							accessibilityRole="button"
							icon="menu"
							onPress={() => navigation.toggleDrawer()}
						/>
					)}
					{showCommandBar && !isMobile && (
						<Box paddingLeft={12}>
							<CommandBar />
						</Box>
					)}
				</Box>
			)}

			{headerLeft?.()}
			{back && !headerLeft && (
				<Appbar.BackAction
					accessibilityLabel="Go back"
					accessibilityRole="button"
					onPress={() => {
						if (router.canGoBack()) {
							router.back();
						} else {
							router.push("/(protected)/(tabs)/home");
						}
					}}
				/>
			)}
			<Appbar.Content
				title={
					<TitleContent
						align={finalAlign}
						headerTitle={headerTitle}
						subtitle={subtitle}
						stats={stats}
						breadcrumbs={breadcrumbs}
						renderSearchbar={renderSearchbar}
					/>
				}
			/>
			{renderHeaderRight}
			{isWeb && (
				<FeedbackDialog
					visible={showFeedbackDialog}
					view={pathname}
					feedbackSubtitle={t(defaultFeedbackSubtitle)}
					handleDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</Appbar.Header>
	);
};

export default CustomNavigationBar;
